import { useRouter } from "next/router";

import BaseBottomSheet from "@domains/common/views/modal/BaseBottomSheet/BaseBottomSheet";
import EventViewModel from "@domains/event/viewModel/EventViewModel";
import useModalState from "@logics/hooks/useModalState";
import { Typo } from "@enkor/design/components";
import { grayScale, pink } from "@enkor/design/colors";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import { CrownFilledIcon } from "@enkor/design/icons";
import WebViewBridge from "@logics/utils/webViewBridge";
import { dataTrackEvent } from "@logics/utils/dataTrackEvent";
import { DATA_TRACKING_EVENT_NAME } from "@domains/common/constants/dataTrackEventName";

import * as S from "./AvatarListBottomSheet.style";

interface AvatarListBottomSheetProps {
  participant: EventViewModel["participant"];
  hostId: EventViewModel["host"]["id"];
}

const AvatarListBottomSheet = ({
  participant,
  hostId,
}: AvatarListBottomSheetProps) => {
  const { closeModal } = useModalState();
  const { push } = useRouter();

  const moveToUserProfilePage = (participantId: number) => {
    dataTrackEvent({
      eventName: DATA_TRACKING_EVENT_NAME.GUEST_EVENT.CLICK_JOINER_PROFILE,
      properties: participant.list.find(
        ({ userId }) => userId === participantId,
      ),
    });

    if (WebViewBridge.isInAppWebView()) {
      WebViewBridge.getInstance().navigateToUserProfile(participantId);
    } else {
      push({
        pathname: `${NAVIGATION_ROUTE.PROFILE.HREF}/${participantId}`,
        query: NAVIGATION_ROUTE.PROFILE.QUERY,
      });
    }
  };

  return (
    <BaseBottomSheet
      headerTitle={`Members ${participant.displayCountText}`}
      closeBtnType="icon"
      setting={{
        $roundBorder: true,
        $noPadding: true,
      }}
      onClose={closeModal}
    >
      <S.Container>
        {participant.list.map((member) => (
          <S.MemberContainer
            key={member.participantId}
            $isClickable
            onClick={() => moveToUserProfilePage(Number(member.participantId))}
          >
            <S.ImageContainer>
              <S.AvatarImageWrapper>
                <S.MemberAvatar
                  src={member.profileImage || "/assets/image_default9.png"}
                  alt="member profile"
                  width={0}
                  height={0}
                  sizes={`${S.AVATAR_SIZE}px`}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </S.AvatarImageWrapper>
              {hostId === member.participantId && (
                <S.HostMark>
                  <CrownFilledIcon size={12} strokeColor={pink.main} />
                </S.HostMark>
              )}
            </S.ImageContainer>
            <S.MemberInfoWrap>
              <Typo.Text name="strongM" color={grayScale.white}>
                {member.name}
              </Typo.Text>
              <Typo.Text name="s" color={grayScale[500]}>
                {member.nationalWithFlag}
              </Typo.Text>
            </S.MemberInfoWrap>
          </S.MemberContainer>
        ))}
      </S.Container>
    </BaseBottomSheet>
  );
};

export default AvatarListBottomSheet;
